import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { motion } from "motion/react";
import type { UnsubscribeInfo } from "../../shared/types.ts";
import { Button, Logo, MarkLoader, rise, staggerParent } from "../ui/index.ts";
import { ApiError, errorText, getUnsubscribe, unsubscribe } from "../lib/api.ts";

type Kind = NonNullable<UnsubscribeInfo["kind"]>;

/** What each kind is called, and the emails turning it off stops. */
const KINDS: Record<Kind, { name: string; stops: string }> = {
  setup_reminder: { name: "setup reminders", stops: "reminders to finish setting up your headcount account" },
  application_updates: { name: "application updates", stops: "emails when an application needs your answer" },
  limit_reached: { name: "free plan limit emails", stops: "emails when you've used all your free applications for the month" },
  new_matches: { name: "job match updates", stops: "emails about new jobs that match your preferences" },
  job_alerts: { name: "job alerts", stops: "job alert emails from headcount" },
};

type Stage = { at: "loading" } | { at: "down" } | { at: "invalid" } | { at: "ask"; kind: Kind } | { at: "done"; kind: Kind };

/**
 * An email's unsubscribe link, signed in or out. Loading the page only reads what the link turns off; the button does
 * it, since mail scanners open every link in a message. A job alert subscriber has no account, so no preferences link.
 */
export function Unsubscribe() {
  const [, navigate] = useLocation();
  const [token] = useState(() => new URLSearchParams(location.search).get("t") ?? "");
  const [stage, setStage] = useState<Stage>(() => (token ? { at: "loading" } : { at: "invalid" }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!token) return;
    setStage({ at: "loading" });
    getUnsubscribe(token).then(
      (info) => setStage(info.kind ? { at: "ask", kind: info.kind } : { at: "invalid" }),
      () => setStage({ at: "down" }),
    );
  }, [token]);

  useEffect(load, [load]);

  const confirm = async (kind: Kind) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await unsubscribe(token);
      setStage({ at: "done", kind });
    } catch (e) {
      if (e instanceof ApiError && e.error === "bad_token") setStage({ at: "invalid" });
      else setError(errorText(e, "You weren't unsubscribed. Try again."));
    }
    setBusy(false);
  };

  const prefs = (
    <Link href="/settings/emails" className="font-semibold text-primary-ink underline-offset-4 hover:underline">
      Manage email preferences
    </Link>
  );

  let page: ReactNode;
  if (stage.at === "loading") page = <MarkLoader delay={0.4} />;
  else if (stage.at === "down")
    page = (
      <Column step="down" title="Something went wrong." text="Check your connection and try again.">
        <Button size="lg" variant="secondary" onClick={load}>
          Try again
        </Button>
      </Column>
    );
  else if (stage.at === "invalid")
    page = (
      <Column step="invalid" title="This link is invalid or has expired." text="You can choose which emails you get in your email preferences.">
        <Button size="lg" onClick={() => navigate("/settings/emails")}>
          Manage email preferences
        </Button>
      </Column>
    );
  else if (stage.at === "ask")
    page = (
      <Column step="ask" title={`Unsubscribe from ${KINDS[stage.kind].name}?`} text={`You'll stop getting ${KINDS[stage.kind].stops}.`}>
        <Button size="lg" loading={busy} onClick={() => confirm(stage.kind)}>
          Unsubscribe
        </Button>
        {error && (
          <p className="text-sm text-bad-ink" role="alert">
            {error}
          </p>
        )}
        {stage.kind !== "job_alerts" && <p className="text-[15px]">{prefs}</p>}
      </Column>
    );
  else
    page = (
      <Column step="done" title="You're unsubscribed." text={`You'll no longer get ${KINDS[stage.kind].stops}.`}>
        {stage.kind !== "job_alerts" && <p className="text-[15px]">{prefs}</p>}
      </Column>
    );

  return (
    <main className="flex min-h-dvh flex-col bg-page px-5 pb-8 pt-6 md:px-10">
      <Link href="/" aria-label="headcount home" className="self-start rounded-control">
        <Logo size={22} />
      </Link>
      <div className="grid flex-1 place-items-center pb-16">{page}</div>
    </main>
  );
}

/** A centered column for an email link's page: a title, a line under it, then the actions. */
export function Column({ step, title, text, children }: { step: string; title: string; text: ReactNode; children: ReactNode }) {
  return (
    <motion.div key={step} className="flex w-full max-w-[400px] flex-col gap-6" variants={staggerParent(0.06)} initial="hidden" animate="show">
      <motion.div variants={rise} className="flex flex-col gap-2">
        <h1 className="text-balance font-display text-[34px] leading-[1.08] text-ink">{title}</h1>
        <p className="text-pretty text-[15px] text-muted">{text}</p>
      </motion.div>
      <motion.div variants={rise} className="flex flex-col gap-4">
        {children}
      </motion.div>
    </motion.div>
  );
}
